// src/components/ConfigProvider.js - ENHANCED VERSION
// Enhanced with secure configuration while maintaining 100% existing functionality

import { createContext, useContext, useEffect, useState } from 'react';
import { apiConfig } from '../config/apiConfig';
import { siteConfig } from '../config/siteConfig';
import { themeConfig } from '../config/themeConfig';
import { features } from '../config/features';

// ENHANCEMENT: Import secure configuration services
import { ConfigServiceEnhancer } from '../services/enhancers/ConfigServiceEnhancer';
import { ErrorTrackingService } from '../services/ErrorTrackingService';

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  // ENHANCEMENT: Add state for configuration loading and status
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [enhancedConfig, setEnhancedConfig] = useState(null);

  // ENHANCEMENT: Initialize secure configuration on mount
  useEffect(() => {
    const initializeSecureConfig = async () => {
      try {
        console.log('🔧 Initializing secure configuration...');
        
        // Initialize ConfigService
        const initSuccess = await ConfigServiceEnhancer.initializeConfig();
        
        if (initSuccess) {
          // Get enhanced configuration
          const enhanced = ConfigServiceEnhancer.getConfigForProvider();
          setEnhancedConfig(enhanced);
          
          console.log('✅ Secure configuration initialized successfully');
          ErrorTrackingService.addBreadcrumb(
            'ConfigProvider initialized with secure configuration',
            'config',
            'info',
            { configSource: enhanced._source }
          );
        } else {
          console.warn('⚠️ Secure configuration initialization failed, using fallback');
          setConfigError('Secure configuration initialization failed');
        }
        
      } catch (error) {
        console.error('❌ ConfigProvider initialization error:', error);
        setConfigError(error.message);
        
        ErrorTrackingService.captureError(error, {
          component: 'ConfigProvider',
          method: 'initializeSecureConfig'
        });
      } finally {
        setConfigLoaded(true);
      }
    };

    initializeSecureConfig();
  }, []);

  // ENHANCEMENT: Create configuration object with secure and fallback support
  const config = {
    // EXISTING CONFIGURATION (UNCHANGED) - Always available
    site: siteConfig,
    theme: themeConfig,
    api: enhancedConfig?.api || apiConfig, // Use enhanced if available, fallback to original
    features,
    
    // ENHANCEMENT: Add configuration metadata
    _meta: {
      loaded: configLoaded,
      error: configError,
      enhanced: !!enhancedConfig,
      source: enhancedConfig?._source || 'fallback',
      timestamp: enhancedConfig?._timestamp || new Date().toISOString()
    }
  };

  // ENHANCEMENT: Add development utilities (only in development)
  if (process.env.NODE_ENV === 'development') {
    config._dev = {
      refreshConfig: async () => {
        try {
          const refreshed = ConfigServiceEnhancer.refreshConfiguration();
          if (refreshed) {
            const enhanced = ConfigServiceEnhancer.getConfigForProvider();
            setEnhancedConfig(enhanced);
            console.log('🔄 Configuration refreshed successfully');
            return true;
          }
          return false;
        } catch (error) {
          console.error('❌ Configuration refresh failed:', error);
          return false;
        }
      },
      
      getConfigStatus: () => {
        return ConfigServiceEnhancer.getConfigurationStatus();
      },
      
      validateConfig: () => {
        return ConfigServiceEnhancer.validateConfiguration();
      }
    };
  }

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
};

// ENHANCEMENT: Enhanced useConfig hook with additional utilities
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  
  // ENHANCEMENT: Add configuration utilities to the hook
  return {
    ...context,
    
    // Utility functions
    isConfigLoaded: () => context._meta.loaded,
    hasConfigError: () => !!context._meta.error,
    getConfigSource: () => context._meta.source,
    isEnhanced: () => context._meta.enhanced,
    
    // Firebase-specific utilities
    getFirebaseConfig: () => context.api.firebase,
    isFirebaseSecure: () => context._meta.source === 'environment',
    
    // Development utilities (only available in development)
    ...(process.env.NODE_ENV === 'development' && context._dev ? {
      dev: context._dev
    } : {})
  };
};

// ENHANCEMENT: Configuration status hook for monitoring
export const useConfigStatus = () => {
  const config = useConfig();
  
  return {
    loaded: config.isConfigLoaded(),
    error: config.hasConfigError(),
    source: config.getConfigSource(),
    enhanced: config.isEnhanced(),
    secure: config.isFirebaseSecure(),
    timestamp: config._meta.timestamp
  };
};

// ENHANCEMENT: Firebase configuration hook for easy access
export const useFirebaseConfig = () => {
  const config = useConfig();
  
  return {
    config: config.getFirebaseConfig(),
    isSecure: config.isFirebaseSecure(),
    source: config.getConfigSource()
  };
};

// BACKWARD COMPATIBILITY: Export original useConfig for existing code
export const useConfigLegacy = () => {
  return {
    site: siteConfig,
    theme: themeConfig,
    api: apiConfig
  };
};
